import type { ProviderStatus } from '@contracts/market';
import type { ConnectionState } from './ws';

export type FeedViewState =
  | 'initial'
  | 'backend_unavailable'
  | 'reconnecting'
  | 'not_configured'
  | 'auth_failed'
  | 'rate_limited'
  | 'disconnected'
  | 'awaiting_data'
  | 'fallback'
  | 'stale'
  | 'delayed'
  | 'replay'
  | 'live';

export type Tone = 'live' | 'replay' | 'delayed' | 'stale' | 'disconnected' | 'fallback' | 'neutral';

export interface FeedView {
  state: FeedViewState;
  /** Badge text. Never "LIVE DATA" unless the backend reports an undelayed live feed that is fresh. */
  label: string;
  tone: Tone;
  title: string;
  message: string;
  /** True when the displayed price must not be trusted as current. */
  isStale: boolean;
  marketOpen: boolean | null;
}

/**
 * Derives what the dashboard should say about the data feed.
 * Combines provider status (backend) with the client's own view of the socket
 * and quote age so a frozen socket can never show a confident price.
 */
export function deriveFeedView(
  provider: ProviderStatus | null,
  connection: ConnectionState,
  quoteReceivedAt: number | null,
  now: number,
  backendUp = true,
): FeedView {
  const marketOpen = provider ? provider.marketOpen : null;
  if (!backendUp) {
    return {
      state: 'backend_unavailable',
      label: 'DISCONNECTED',
      tone: 'disconnected',
      title: 'Backend unavailable',
      message: 'The MagicTradie! API cannot be reached. Prices shown, if any, are not current.',
      isStale: true,
      marketOpen,
    };
  }
  if (!provider) {
    if (connection === 'reconnecting') {
      return {
        state: 'reconnecting',
        label: 'DISCONNECTED',
        tone: 'disconnected',
        title: 'Reconnecting to the market stream',
        message: 'Retrying with backoff. Prices and the chart resume when the feed is restored.',
        isStale: true,
        marketOpen,
      };
    }
    return {
      state: 'initial',
      label: 'CONNECTING',
      tone: 'neutral',
      title: 'Connecting to market data',
      message: 'Opening the internal stream and loading provider status.',
      isStale: true,
      marketOpen,
    };
  }
  if (connection === 'reconnecting' || connection === 'closed') {
    return {
      state: 'reconnecting',
      label: 'DISCONNECTED',
      tone: 'disconnected',
      title: 'Reconnecting to the market stream',
      message: 'The dashboard lost its live connection. The last price below may be outdated.',
      isStale: true,
      marketOpen,
    };
  }
  const code = provider.errorCode;
  if (provider.feedState === 'DISCONNECTED') {
    if (code === 'not_configured') {
      return {
        state: 'not_configured',
        label: 'DISCONNECTED',
        tone: 'disconnected',
        title: 'No market-data provider configured',
        message:
          'Set MARKET_DATA_PROVIDER, MARKET_DATA_API_KEY and MARKET_DATA_ACCOUNT_ID on the backend, or start with MARKET_DATA_MODE=replay for labelled replay data.',
        isStale: true,
        marketOpen,
      };
    }
    if (code === 'auth_failed') {
      return {
        state: 'auth_failed',
        label: 'DISCONNECTED',
        tone: 'disconnected',
        title: 'Provider authentication failed',
        message: 'The provider rejected the configured credentials. Check MARKET_DATA_API_KEY and account id.',
        isStale: true,
        marketOpen,
      };
    }
    if (code === 'rate_limited') {
      return {
        state: 'rate_limited',
        label: 'DISCONNECTED',
        tone: 'disconnected',
        title: 'Provider rate limit reached',
        message: 'Requests are paused until the provider allows more. Signals are suspended.',
        isStale: true,
        marketOpen,
      };
    }
    if (provider.awaitingFirstQuote) {
      return {
        state: 'awaiting_data',
        label: 'CONNECTING',
        tone: 'neutral',
        title: 'Waiting for the first quote',
        message: 'Connected to the provider; no price received yet.',
        isStale: true,
        marketOpen,
      };
    }
    return {
      state: 'disconnected',
      label: 'DISCONNECTED',
      tone: 'disconnected',
      title: 'Market data disconnected',
      message: provider.errorMessage ?? 'Reconnecting with exponential backoff.',
      isStale: true,
      marketOpen,
    };
  }
  const staleAfterMs = provider.staleAfterSeconds * 1000;
  const clientAge = quoteReceivedAt === null ? Infinity : now - quoteReceivedAt;
  if (provider.feedState === 'STALE' || clientAge > staleAfterMs) {
    return {
      state: 'stale',
      label: 'STALE DATA',
      tone: 'stale',
      title: provider.marketOpen ? 'Price feed is stale' : 'Market closed — price feed is stale',
      message: 'No fresh quote within the allowed age. The displayed price is not current.',
      isStale: true,
      marketOpen,
    };
  }
  if (provider.feedState === 'SIMULATED_FALLBACK') {
    return {
      state: 'fallback',
      label: 'SIMULATED FALLBACK',
      tone: 'fallback',
      title: 'SIMULATED FALLBACK — not real market prices',
      message: 'The live provider is down and simulated fallback is enabled. Prices are NOT real.',
      isStale: false,
      marketOpen,
    };
  }
  if (provider.feedState === 'DELAYED') {
    return {
      state: 'delayed',
      label: 'DELAYED DATA',
      tone: 'delayed',
      title: 'Delayed market data',
      message: 'Quotes arrive later than the real-time threshold. Signals are suspended while delayed.',
      isStale: false,
      marketOpen,
    };
  }
  if (provider.feedState === 'REPLAY') {
    return {
      state: 'replay',
      label: 'REPLAY DATA',
      tone: 'replay',
      title: 'Replay data',
      message: 'Deterministic replay for development and testing — not live market prices.',
      isStale: false,
      marketOpen,
    };
  }
  return {
    state: 'live',
    label: 'LIVE DATA',
    tone: 'live',
    title: 'Live market data',
    message: `Streaming from ${provider.provider}.`,
    isStale: false,
    marketOpen,
  };
}
